import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { Text } from 'tio-ui/components/typography';
import { or } from 'tio-ui/utilities';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import Button from 'tio-reimagined/components/button';
import { PhWarning, PhCloudArrowUp, PhMapPin, PhEnvelope } from 'ember-phosphor-icons';

/**
 * PaymentAlertCard — Reimagined version of SLR account alerts
 *
 * Shows payment validation errors and account issues with contextual
 * resolution actions (upload document, select address, contact support).
 *
 * @example
 * ```gts
 * <PaymentAlertCard
 *   @alerts={{this.accountAlerts}}
 *   @validationErrors={{this.paymentValidationErrors}}
 *   @isSlrEligible={{true}}
 * />
 * ```
 */
interface PaymentAlertCardSignature {
  Args: {
    /** Account-level alerts from the employee service */
    alerts?: { content: string; uploadDocument?: boolean; addressSelect?: boolean }[];
    /** Payment validation error messages */
    validationErrors?: string[];
    /** Whether SLR is eligible at all */
    isSlrEligible?: boolean;
    /** Callback when address selection is requested */
    onSelectAddress?: () => void;
  };
  Element: HTMLDivElement;
}

export default class PaymentAlertCard extends Component<PaymentAlertCardSignature> {
  get alerts() {
    return this.args.alerts ?? [];
  }

  get validationErrors() {
    return this.args.validationErrors ?? [];
  }

  get hasAlerts(): boolean {
    return this.alerts.length > 0 || this.validationErrors.length > 0;
  }

  get hasAddressIssue(): boolean {
    return this.alerts.some((alert) => alert.addressSelect);
  }

  get hasUploadIssue(): boolean {
    return this.alerts.some((alert) => alert.uploadDocument);
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div ...attributes>
      {{#if @isSlrEligible}}
        {{#if this.hasAlerts}}
          <AlertBanner @intent="warning" @title="Action Required">
            <div class="space-y-3">
              {{#if this.validationErrors.length}}
                <div class="space-y-1">
                  {{#each this.validationErrors as |error|}}
                    <div class="flex items-start gap-2">
                      <PhWarning @size={{16}} class="text-amber-500 mt-0.5 flex-shrink-0" />
                      <Text class="text-sm">{{error}}</Text>
                    </div>
                  {{/each}}
                </div>
              {{/if}}

              {{#each this.alerts as |alert|}}
                <div
                  class="flex items-start justify-between gap-4 py-2 border-t border-amber-200 first:border-t-0 first:pt-0"
                >
                  <div class="flex items-start gap-2">
                    <PhWarning @size={{16}} class="text-amber-500 mt-0.5 flex-shrink-0" />
                    <Text class="text-sm">{{alert.content}}</Text>
                  </div>
                  <div class="flex-shrink-0">
                    {{#if alert.uploadDocument}}
                      <LinkTo
                        @route="authenticated.observability.upload"
                        @query={{hash source="slr" providers="statement,nslds"}}
                      >
                        <Button @intent="primary" @size="sm" @icon={{PhCloudArrowUp}}>
                          Upload Statement
                        </Button>
                      </LinkTo>
                    {{else if alert.addressSelect}}
                      {{#if @onSelectAddress}}
                        <Button
                          @intent="primary"
                          @size="sm"
                          @icon={{PhMapPin}}
                          {{on "click" @onSelectAddress}}
                        >
                          Select Address
                        </Button>
                      {{/if}}
                    {{/if}}
                  </div>
                </div>
              {{/each}}

              {{#unless (or this.hasUploadIssue this.hasAddressIssue)}}
                <div class="flex items-center gap-2 pt-2 border-t border-amber-200">
                  <PhEnvelope @size={{16}} class="text-amber-500" />
                  <Text class="text-sm">
                    If this issue persists, please
                    <a href="mailto:support@tuition.io" class="font-medium underline">contact
                      support</a>.
                  </Text>
                </div>
              {{/unless}}
            </div>
          </AlertBanner>
        {{/if}}
      {{else}}
        <AlertBanner @intent="warning" @title="Not Eligible for Contributions">
          Your current plan does not include employer student loan contributions. Contact your HR
          department for more information about available benefits.
        </AlertBanner>
      {{/if}}
    </div>
  </template>
}
